/* Cross-browser plumbing for third-party video embeds.

   Every FlexiFunnels video provider — Gumlet, VdoCipher, YouTube, Vimeo, and
   whatever a course author pastes in — plays inside a cross-origin <iframe>.
   What that frame is ALLOWED to do is decided by the embedding page, and the
   defaults are restrictive, so an embed that works in one browser and not
   another is very often a permissions problem on our side rather than the
   provider's. This module holds that contract in one place, so the raw-snippet
   path and the iframe the player builds itself can never drift apart.

   Referenced against the legacy learner app (Course_Membership
   pages/my-learning.js + components/VdoPlayer.js + components/GumletVdoplayer.js). */

/* Permissions a third-party video embed needs before a browser will let it do
   its job. Everything here is a Permissions-Policy feature that DEFAULTS TO OFF
   inside a cross-origin iframe, so a missing entry is not cosmetic — the frame
   loads and the player then silently cannot start:

     encrypted-media *   EME / DRM. Chromium delegates it only where `allow`
                         says so; the bare form grants it to the frame's own
                         origin, and `*` covers a player that fetches its licence
                         from a nested origin (this is exactly what Gumlet's own
                         oEmbed markup asks for).
     autoplay            required before a player may start playback itself
     fullscreen          Safari and Firefox read `allow="fullscreen"`; the legacy
                         `allowfullscreen` attribute alone is not enough on all
                         of them, which is why BOTH are set
     picture-in-picture  the PiP control in the provider's own chrome
     clipboard-write     "copy link" in the provider's share menu
     accelerometer/gyroscope  360°/VR assets

   Kept as one string so every embed path — the raw snippet and the iframe we
   build ourselves — grants exactly the same set. */
export const EMBED_ALLOW = "accelerometer; autoplay; clipboard-write; encrypted-media *; gyroscope; picture-in-picture; fullscreen";

/* The YouTube lesson player gets the same set MINUS clipboard-write.

   Its settings gear is deliberately left reachable — it is the only place a
   viewer can pick quality, speed or captions — and the sheet that opens carries
   a "More options" row whose panel is Copy video URL / Copy video URL at current
   time / Copy embed code. On a narrow player that sheet scrolls, so no click
   shield can promise the row is covered (see YT_BAND_* in CourseViews).
   Withholding the permission kills the copy wherever the row is reached:
   verified against a live embed — with the feature granted the click leaves
   `https://youtu.be/<id>` on the clipboard, without it the clipboard is
   untouched and YouTube has no execCommand fallback.

   YouTube itself needs nothing else from it; the share button that would use it
   is under a shield already. Other providers keep the feature — their own share
   menus are the reason it is in EMBED_ALLOW at all. */
export const YT_EMBED_ALLOW = EMBED_ALLOW
    .split(";").map((d) => d.trim()).filter((d) => d && !/^clipboard-write\b/i.test(d)).join("; ");

/* Union a provider's own `allow` list with EMBED_ALLOW, one entry per feature.
   The provider's directive wins unless ours is strictly wider (an explicit `*`
   allowlist beating an implicit self), so nothing a provider deliberately
   scoped gets widened by accident. */
export function mergeAllow(existing) {
    const feature = (d) => d.split(/\s+/)[0].toLowerCase();
    const merged = new Map();
    String(existing || "").split(";").map((d) => d.trim()).filter(Boolean)
        .forEach((d) => merged.set(feature(d), d));
    EMBED_ALLOW.split(";").map((d) => d.trim()).filter(Boolean).forEach((d) => {
        const cur = merged.get(feature(d));
        if (!cur || (d.includes("*") && !cur.includes("*"))) merged.set(feature(d), d);
    });
    return Array.from(merged.values()).join("; ");
}

/** src of the first <iframe> in an embed snippet — the URL that same video can
 *  be opened at directly when the frame itself is what a browser refuses. */
export function firstIframeSrc(html) {
    const m = /<iframe\b[^>]*\ssrc="([^"]+)"/i.exec(String(html || ""));
    return m ? m[1] : null;
}

/* Providers whose JS API performs a real handshake, so silence from them is
   EVIDENCE the embed is not running (see the health watchdog). Deliberately
   only the two FlexiFunnels-hosted players — an author-pasted embed from an
   arbitrary provider never answers, and treating that as a failure would put a
   warning under a video that plays perfectly well. */
export const WATCHABLE_EMBED = /play\.gumlet\.io|player\.vdocipher\.com/i;
/* How long to wait for that handshake. Long enough to cover a slow first load
   of the provider's bundle on a poor connection. */
export const EMBED_HEALTH_MS = 9000;

// Normalise a raw provider embed snippet (VdoCipher / Gumlet DRM / custom embed)
// for inline rendering: ensure fullscreen and the rest of EMBED_ALLOW are
// granted, and that the iframe fills the 16:9 wrapper. Matches the reference
// my-learning embed handling.
export function normalizeEmbed(html) {
    if (!html) return html;
    let out = String(html);
    if (!/allowfullscreen/i.test(out)) out = out.replace(/<iframe/i, "<iframe allowfullscreen");
    if (/<iframe\b[^>]*\sallow\s*=\s*"/i.test(out)) {
        out = out.replace(/(<iframe\b[^>]*\sallow\s*=\s*")([^"]*)(")/i, (m, pre, val, post) => pre + mergeAllow(val) + post);
    } else {
        out = out.replace(/<iframe/i, `<iframe allow="${EMBED_ALLOW}"`);
    }
    out = out.replace(/<iframe/i, '<iframe style="position:absolute;inset:0;width:100%;height:100%;border:0"');
    // A YouTube iframe inside a raw embed (generic_video "embed") needs
    // enablejsapi=1 so the IFrame API can read current time for note timestamps —
    // author-pasted embeds usually omit it. (see videoTime.js / embedUrl.)
    out = out.replace(/(<iframe\b[^>]*\bsrc=")([^"]*youtube\.com\/embed\/[^"]*)(")/i, (m, pre, src, post) => {
        if (/enablejsapi=1/.test(src)) return m;
        return pre + src + (src.includes("?") ? "&" : "?") + "enablejsapi=1" + post;
    });
    return out;
}
/* A source the browser's own <video> takes directly. `.mpd` (DASH) is in here
   for routing reasons rather than because every browser decodes it: the
   reference handed it to its <video> player as application/dash+xml, and the
   only alternative here is the generic-embed iframe, which would render a
   manifest's XML instead of a player. A <video> at least fails as a video. */
